import pageContextData, {
    GlobalizationData,
    ClientProviderData,
    FeaturesData,
    InfraData,
    SmartBannerData,
    TouchIdData,
    UrlResourceData,
    VisualData,
    ReleaseData,
    SiteData,
    AirshipData,
    BrandData,
    DeviceData,
    EnvironmentData,
    GeoComplyData,
    SurveyData,
    ThemeData,
    NativeData,
    PageContextData,
    MigrationData,
    OptimizelyData,
} from 'page-context-data';

import {
    AuthenticatedPageContextData,
    AuthenticatedSessionData,
    AuthenticatedUserData,
    CombinedLocalizationData,
    CombinedNetworkData,
    CombinedPageContextData,
    CombinedRegulationData,
    RecursivePartial,
} from 'page-context-manager';
import { LoggerFactory } from '../../logger';
import { IPageContextManager } from './models/IPageContextManager';
import merge from 'lodash/merge';
import cloneDeep from 'lodash/cloneDeep';
import { ILogger } from '@sparkware/uc-sdk-core';

class PageContextManager implements IPageContextManager {
    private _authenticatedPageContextData: AuthenticatedPageContextData = {};
    private _logger: ILogger;
    private _combinedPageContextData: CombinedPageContextData;

    constructor(logger: ILogger) {
        this._logger = logger;
        this._combinedPageContextData = pageContextData as CombinedPageContextData;
    }

    public updatePageContext = (updates: RecursivePartial<PageContextData>): any => {
        this._logger.debug(
            `updatePageContext: updating page context data with the following information: `,
            updates,
        );

        merge(pageContextData, pageContextData, updates);
        this._combinedPageContextData = merge(
            {},
            pageContextData,
            this._authenticatedPageContextData,
        );
    };

    public updatePageContextAuthenticated = (
        updates: Partial<AuthenticatedPageContextData>,
    ): void => {
        this._logger.debug(
            `updatePageContextAuthenticated: updating page context data with the following user information: `,
            updates,
        );
        merge(this._authenticatedPageContextData, updates);
        this._combinedPageContextData = merge(
            {},
            pageContextData,
            this._authenticatedPageContextData,
        );
    };

    public removePageContext = (): void => {
        this._logger.debug('removePageContext: removing page context data');

        Object.keys({ ...pageContextData }).forEach((key) => {
            delete pageContextData[key];
        });

        this._combinedPageContextData = merge(
            {},
            pageContextData,
            this._authenticatedPageContextData,
        );
    };

    public removePageContextAuthenticated = (): void => {
        this._logger.debug(
            'removePageContextAuthenticated: removing authenticated page context data',
        );
        this._authenticatedPageContextData = {};
        this._combinedPageContextData = merge(
            {},
            pageContextData,
            this._authenticatedPageContextData,
        );
    };

    public getGlobalizationData = (): GlobalizationData =>
        cloneDeep(this._withDefault(this._combinedPageContextData.globalization));

    public getRegulationData = (): CombinedRegulationData =>
        cloneDeep(this._withDefault(this._combinedPageContextData.regulation));

    public getLocalizationData = (): CombinedLocalizationData =>
        cloneDeep(this._withDefault(this._combinedPageContextData.localization));

    public getDeviceData = (): DeviceData =>
        cloneDeep(this._withDefault(this._combinedPageContextData.device));

    public getAirshipData = (): AirshipData =>
        cloneDeep(this._withDefault(this._combinedPageContextData.airship));

    public getGeoComplyData = (): GeoComplyData =>
        cloneDeep(this._withDefault(this._combinedPageContextData.geoComply));

    public getFeaturesData = (): FeaturesData =>
        cloneDeep(this._withDefault(this._combinedPageContextData.features));

    public getThemeData = (): ThemeData =>
        cloneDeep(this._withDefault(this._combinedPageContextData.theme));

    public getSurveyData = (): SurveyData =>
        cloneDeep(this._withDefault(this._combinedPageContextData.survey));

    public getEnvironmentData = (): EnvironmentData =>
        cloneDeep(this._withDefault(this._combinedPageContextData.environment));

    public getBrandData = (): BrandData =>
        cloneDeep(this._withDefault(this._combinedPageContextData.brand));

    public getReleaseData = (): ReleaseData =>
        cloneDeep(this._withDefault(this._combinedPageContextData.release));

    public getSiteData = (): SiteData =>
        cloneDeep(this._withDefault(this._combinedPageContextData.site));

    public getNativeData = (): NativeData =>
        cloneDeep(this._withDefault(this._combinedPageContextData.native));

    public getTouchIdData = (): TouchIdData =>
        cloneDeep(this._withDefault(this._combinedPageContextData.touchId));

    public getClientProviderData = (): ClientProviderData =>
        cloneDeep(this._withDefault(this._combinedPageContextData.clientProvider));

    public getSmartBannerData = (): SmartBannerData =>
        cloneDeep(this._withDefault(this._combinedPageContextData.smartBanner));

    public getOptimizelyData = (): OptimizelyData =>
        cloneDeep(this._withDefault(this._combinedPageContextData.optimizely));

    public getUrlResourceData = (): UrlResourceData =>
        cloneDeep(this._withDefault(this._combinedPageContextData.urlResource));

    public getInfraData = (): InfraData =>
        cloneDeep(this._withDefault(this._combinedPageContextData.infra));

    public getVisualData = (): VisualData =>
        cloneDeep(this._withDefault(this._combinedPageContextData.visual));

    public getNetworkData = (): CombinedNetworkData =>
        cloneDeep(this._withDefault(this._combinedPageContextData.network));

    public getUserData = (): AuthenticatedUserData =>
        cloneDeep(this._withDefault(this._combinedPageContextData.user));

    public getSessionData = (): AuthenticatedSessionData =>
        cloneDeep(this._withDefault(this._combinedPageContextData.session));

    public getMigrationData = (): MigrationData =>
        cloneDeep(this._withDefault(this._combinedPageContextData.migration));

    private _withDefault = <T>(value: T, defaultValue: T = {} as T): T => {
        return value ?? defaultValue;
    };
}

const logger = LoggerFactory.create('PageContextManager', window.console);

const Instance: IPageContextManager = new PageContextManager(logger);

/**
 * !! Important !!
 * only function that exposed here will be able to be used under the window container
 */
export const {
    getGlobalizationData,
    getMigrationData,
    getRegulationData,
    getLocalizationData,
    getDeviceData,
    getAirshipData,
    getGeoComplyData,
    getFeaturesData,
    getThemeData,
    getSurveyData,
    getEnvironmentData,
    getBrandData,
    getNativeData,
    getSiteData,
    getTouchIdData,
    getClientProviderData,
    getSmartBannerData,
    getOptimizelyData,
    getUrlResourceData,
    getReleaseData,
    getInfraData,
    getVisualData,
    getNetworkData,
    getUserData,
    getSessionData,
    updatePageContext,
    updatePageContextAuthenticated,
    removePageContextAuthenticated,
    removePageContext,
} = Instance;

export default Instance;
